import { useMemo } from 'react';

interface SliderProps {
  label?: string;
  initialValue: number;
  value: number;
  onChange?: (value: number) => void;
  step?: number;
  disabled?: boolean;
}

export function Slider({
  label,
  initialValue,
  value,
  onChange,
  step = 5,
  disabled = false,
}: SliderProps): JSX.Element {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value, 10);
    onChange?.(newValue);
  };

  const getProgressText = (): string => {
    if (value === initialValue) return '--';
    const difference = value - initialValue;
    return `${difference > 0 ? '+' : ''}${difference}%`;
  };

  const getProgressColor = (): string => {
    if (value > initialValue) return 'text-[#1677FF]';
    if (value < initialValue) return 'text-[#FA541C]';
    return '';
  };

  const sliderBackground = useMemo((): string => {
    if (value > initialValue) {
      return `linear-gradient(to right,
        #93c5fd ${initialValue}%,
        #1677FF ${initialValue}%,
        #1677FF ${value}%,
        #FFF2E8 ${value}%
      )`;
    }
    if (value < initialValue) {
      return `linear-gradient(to right,
        #93c5fd ${value}%,
        #CE3F4F ${value}%,
        #CE3F4F ${initialValue}%,
        #FFF2E8 ${initialValue}%
      )`;
    }
    return `linear-gradient(to right, #93c5fd ${value}%, #FFF2E8 ${value}%)`;
  }, [value, initialValue]);

  return (
    <div className="flex flex-col items-start self-stretch gap-3">
      <div className="flex justify-between w-full">
        <span className="text-[#434343] font-body text-base font-normal leading-[140%]">
          {label}
        </span>
        <span className="font-body text-base font-semibold text-[#141414] leading-[140%]">
          {value}%
        </span>
      </div>

      <div className="relative w-full h-2 mb-6">
        <input
          type="range"
          min="0"
          max="100"
          step={step}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className="w-full h-2 appearance-none bg-[#FFF2E8] rounded-full [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#361D50] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#361D50] [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:cursor-pointer"
          style={{
            background: sliderBackground,
          }}
        />
      </div>

      <span className="font-body text-sm text-[#8C8C8C] leading-[140%]">
        <span className={getProgressColor()}>{getProgressText()}</span> Progress
        achieved since last checkin on this ticket.
      </span>
    </div>
  );
}
